import zod from 'zod'

import { SetAddOnDTO } from '../server/database/models/AddOnModel.js'
import AdminPriceListLogModel from '../server/database/models/AdminPriceListLogModel.js'
import FulfillEventModel from '../server/database/models/FulfillEventModel.js'
import { LocaleRow } from '../server/database/models/LocaleModel.js'
import ReturnEventModel from '../server/database/models/ReturnEventModel.js'
import { ServiceDTO } from '../server/database/models/ServiceModel.js'
import ShipmentEventModel from '../server/database/models/ShipmentEventModel.js'
import { SubscriptionAccessoryRow } from '../server/database/models/SubscriptionAccessoryModel.js'
import { SubscriptionClaimDamageLocationDTO } from '../server/database/models/SubscriptionClaimDamageLocationModel.js'
import { SubscriptionPaymentLineDTO } from '../server/database/models/SubscriptionPaymentLineModel.js'
import { SubscriptionProductServiceRow } from '../server/database/models/SubscriptionProductServiceModel.js'
import { TradeInDTO } from '../server/database/models/TradeInModel.js'
import { Day } from './day.js'
import {
    BolttechPolicyStatus,
    ClaimDamageType,
    ClaimProductResolution,
    ClaimStatus,
    ClaimType,
    CreditCheckStatus,
    CustomerStatus,
    InvoicingSystem,
    InzmoPolicyStatus,
    LisaAndAgreementType,
    LisaAndRentalAgreementStatus,
    LisaAndValidationReason,
    LisaAndValidationRecommendation,
    LisaAndValidationStatus,
    MessageStatus,
    PayexPaymentOrderStatus,
    PayexTokenType,
    PlanCode,
    PolicyProvider,
    ProductStatus,
    ProductType,
    ReturnEventType,
    SubscriptionFeePeriodType,
    SubscriptionFeeStatus,
    SubscriptionFeeType,
    SubscriptionInvoiceStatus,
    SubscriptionInvoiceType,
    SubscriptionPaymentLineStatus,
    SubscriptionPaymentLineType,
    SubscriptionPaymentStatus,
    SubscriptionPaymentType,
    SubscriptionStatus,
    SupportedCountryCodes,
    VendorNotificationStatus,
} from './enums.js'
import { customZod } from './zod.js'

export const paymentOrderPaidDetailsSchema = zod.object({
    externalNonPaymentToken: zod.string(),
    cardBrand: zod.string(),
    cardType: zod.string(),
    maskedPan: zod.string(),
    expiryDate: zod.string(),
    issuerAuthorizationApprovalCode: zod.string(),
    acquirerTransactionType: zod.string(),
    acquirerStan: zod.string(),
    acquirerTerminalId: zod.string(),
    acquirerTransactionTime: zod.string(),
    transactionInitiator: zod.string(),
    bin: zod.string(),
    paymentAccountReference: zod.string(),
})

export type PaymentOrderPaidDetails = zod.infer<typeof paymentOrderPaidDetailsSchema>

const clientSchema = zod.object({
    personalNumber: zod.string(),
    firstName: zod.string().min(1),
    lastName: zod.string().min(1),
    email: zod.email(),
    phone: zod.string().regex(new RegExp('^[0-9]+$')).min(1),

    terms: zod.boolean().optional(),
    pepTerms: zod.boolean().optional(),
    creditCheckTerms: zod.boolean().optional(),
    marketingTerms: zod.boolean().optional(),
    authenticationMethod: zod.string(),
    shippingMethodUuid: zod.uuid(),

    country: zod.enum(SupportedCountryCodes),
    postalCode: zod.string().min(1),
    address: zod.string().min(1).max(40),
    city: zod.string().min(1),
    // TEMP: Lock shipping if defined by scrive
    postalCodeLock: zod.boolean(),
    addressLock: zod.boolean(),
    cityLock: zod.boolean(),

    differentShippingAddress: zod.literal<boolean>(false),
    shippingPostalCode: zod.string(),
    shippingAddress: zod.string().max(40),
    shippingCity: zod.string(),
})
const swedishClientBasicSchema = zod.object({
    ...clientSchema.shape,
    country: zod.literal<string>(SupportedCountryCodes.SWEDEN),
    postalCode: zod.string().min(5).max(5),
})

const swedishClientDifferentSchema = zod.object({
    ...swedishClientBasicSchema.shape,
    differentShippingAddress: zod.literal<boolean>(true),
    shippingPostalCode: zod.string().min(5).max(5),
    shippingAddress: zod.string().min(1).max(40),
    shippingCity: zod.string().min(1),
})

const finnishClientBasicSchema = zod.object({
    ...clientSchema.shape,
    country: zod.literal<string>(SupportedCountryCodes.FINLAND),
    postalCode: zod.string().min(5).max(5),
})

const finnishClientDifferentSchema = zod.object({
    ...finnishClientBasicSchema.shape,
    differentShippingAddress: zod.literal<boolean>(true),
    shippingPostalCode: zod.string().min(5).max(5),
    shippingAddress: zod.string().min(1).max(40),
    shippingCity: zod.string().min(1),
})

const norwegianClientBasicSchema = zod.object({
    ...clientSchema.shape,
    country: zod.literal<string>(SupportedCountryCodes.NORWAY),
    postalCode: zod.string().min(4).max(4),
})

const norwegianClientDifferentSchema = zod.object({
    ...norwegianClientBasicSchema.shape,
    differentShippingAddress: zod.literal<boolean>(true),
    shippingPostalCode: zod.string().min(4).max(4),
    shippingAddress: zod.string().min(1).max(40),
    shippingCity: zod.string().min(1),
})

const danishClientBasicSchema = zod.object({
    ...clientSchema.shape,
    country: zod.literal<string>(SupportedCountryCodes.DENMARK),
    postalCode: zod.string().min(4).max(4),
})

const danishClientDifferentSchema = zod.object({
    ...danishClientBasicSchema.shape,
    differentShippingAddress: zod.literal<boolean>(true),
    shippingPostalCode: zod.string().min(4).max(4),
    shippingAddress: zod.string().min(1).max(40),
    shippingCity: zod.string().min(1),
})

export const calculationResult = zod.object({
    variation_type: zod.enum(LisaAndAgreementType),
    stock_monthly_price: zod.number(),
    monthly_discount: zod.number(),
    discount_duration: zod.number(),
    leftover_discount: zod.number(),
    trade_in_discount: zod.number(),
    trade_in_duration: zod.number(),
    leftover_trade_in: zod.number(),
    total_monthly_discount: zod.number(),
    price_after_discounts: zod.number(),
    total_duration: zod.number(),
    price_after_discounts_without_vat: zod.number().optional(),
})

export const calculationResponseSchema = zod.array(calculationResult)

export type CalculationResult = zod.infer<typeof calculationResult>
export type CalculationResponse = zod.infer<typeof calculationResponseSchema>

export const baseClientFormDataSchema = zod.union([
    swedishClientBasicSchema,
    swedishClientDifferentSchema,
    finnishClientBasicSchema,
    finnishClientDifferentSchema,
    norwegianClientBasicSchema,
    norwegianClientDifferentSchema,
    danishClientBasicSchema,
    danishClientDifferentSchema,
])

export const nameCheck: zod.core.CheckFn<ClientFormData> = (ctx) => {
    const fullName = ctx.value.firstName + ' ' + ctx.value.lastName

    if (fullName.length > 40) {
        ctx.issues.push({
            path: [ctx.value.firstName.length > ctx.value.lastName.length ? 'firstName' : 'lastName'],
            code: 'too_big',
            maximum: 40,
            type: 'string',
            inclusive: true,
            input: ctx.value,
            origin: 'string',
        })
    }
}

export const clientFormDataSchema = baseClientFormDataSchema.check(nameCheck)

export type ClientFormData = zod.infer<typeof clientFormDataSchema>

export enum BasicViewPaths {
    AUTH_ERROR = 'auth-error',
    FLOW_PICKER = 'flow-picker',
    SIGN_SUCCESS = 'sign-success',
    SIGN_REJECT = 'sign-reject',
    CREDIT_FAILED = 'credit-failed',
    GENERAL_ERROR = 'general-error',
    REDIRECT_TO_SHOP = 'redirect-to-shop',
    ACCESSORY_ERROR = 'accessory-error',
    PRIVACY_POLICY = 'privacy-policy',
}

export enum MockedSubscriptionStatus {
    SIGNED = 'signed',
    AUTHENTICATED = 'authenticated',
    COMPLETED = 'completed',
    ONLYFORUPGRADESIGNED = 'onlyforupgradesigned',
}

export enum SubscriptionSteps {
    PLANS = 'plans',
    AUTH = 'auth',
    SIGN = 'sign',
    FLOW_PICKER = 'flow-picker',
    CREDIT_CHECK = 'credit-check',
    VALIDATION = 'validation',
}

export type ProductReturnEventModel = ReturnEventModel & {
    type: ReturnEventType.PRODUCT
    subscriptionProductUuid: string
}

export type AccessoryReturnEventModel = ReturnEventModel & {
    type: ReturnEventType.ACCESSORY
    subscriptionAccessoryId: string
}

export type ProductFulfillEventModel = FulfillEventModel & {
    type: ReturnEventType.PRODUCT
    subscriptionProductUuid: string
}

export type AccessoryFulfillEventModel = FulfillEventModel & {
    type: ReturnEventType.ACCESSORY
    subscriptionAccessoryId: string
}

export type ProductShipmentEventModel = ShipmentEventModel & {
    type: ReturnEventType.PRODUCT
    subscriptionProductUuid: string
}

export type AccessoryShipmentEventModel = ShipmentEventModel & {
    type: ReturnEventType.ACCESSORY
    subscriptionAccessoryId: string
}

export const isProductReturnEventModel = (returnEvent: ReturnEventModel): returnEvent is ProductReturnEventModel => {
    return returnEvent.type === ReturnEventType.PRODUCT && returnEvent.subscriptionProductUuid !== undefined
}

export const isAccessoryReturnEventModel = (
    returnEvent: ReturnEventModel,
): returnEvent is AccessoryReturnEventModel => {
    return returnEvent.type === ReturnEventType.ACCESSORY && returnEvent.subscriptionAccessoryId !== undefined
}

export const isProductFulfillEventModel = (returnEvent: FulfillEventModel): returnEvent is ProductFulfillEventModel => {
    return returnEvent.type === ReturnEventType.PRODUCT && returnEvent.subscriptionProductUuid !== undefined
}

export const isAccessoryFulfillEventModel = (
    returnEvent: FulfillEventModel,
): returnEvent is AccessoryFulfillEventModel => {
    return returnEvent.type === ReturnEventType.ACCESSORY && returnEvent.subscriptionAccessoryId !== undefined
}

export const isProductShipmentEventModel = (
    returnEvent: ShipmentEventModel,
): returnEvent is ProductShipmentEventModel => {
    return returnEvent.type === ReturnEventType.PRODUCT && returnEvent.subscriptionProductUuid !== undefined
}

export const isAccessoryShipmentEventModel = (
    returnEvent: ShipmentEventModel,
): returnEvent is AccessoryShipmentEventModel => {
    return returnEvent.type === ReturnEventType.ACCESSORY && returnEvent.subscriptionAccessoryId !== undefined
}

interface BaseUpdate {
    priceList: string
    type: 'new' | 'existing'
}

export interface ProductBeingUpdated extends BaseUpdate {
    sku: string
    uuid: string | null
    changes: PlanPriceBeingUpdated[]
}

export interface PlanPriceBeingUpdated extends BaseUpdate {
    title: string
    changes: ChangeDetail[]
    planChangesCount: Record<string, number>
}

export interface ChangeDetail {
    key: string
    oldValue: string
    newValue: string
}

export const pricesFileItemSchema = zod.object({
    SKU: zod.string(),
    'Launch date': zod.string().nullable().optional(),
    Type: zod.string(),
    'Price list': zod.string().nullable().optional(),

    'NO Value': zod.string(),
    'NO Flex': zod.string(),

    'DK Value': zod.string(),
    'DK Flex': zod.string(),

    'SE Value': zod.string(),
    'SE Flex': zod.string(),

    'FI Value': zod.string(),
    'FI Flex': zod.string(),

    'RRP EUR': zod.string(),
    'Coverage Percentage': zod.string(),
    Colour: zod.string(),
    Capacity: zod.string(),

    'Service Fee DKK': zod.string(),
    'Service Fee SEK': zod.string(),
    'Service Fee NOK': zod.string(),
    'Service Fee EUR': zod.string(),

    'Image URL': zod.string().nullable().optional(),
})

export const LogEventSchema = [
    'admin-retry-lisa-and-notified',
    'admin-edit',
    'admin-status-change-with-trigger',
    'admin-status-change-no-trigger',
    'subscription-mit-token-delete',
    'info',
    'unauthorized',
    'cron-error',
    'cron-subscriptions-error',
    'uncaught-error',
    'validation-error',
    'not-found-error',
    'invalid-json',
    'blacklisted-person',
    'whitelisted-person',
    'skipped-credit-check-for-country',
    'new-sqs-message-received',
    'subscription-creation-error',
    'subscription-sqs-update',
    'subscription-status-update',
    'subscription-status-update-error',
    'subscription-status-change-from-completed-to-completed',
    'subscription-presigned-notification-sent',
    'subscription-presigned-notification-error',
    'subscription-signed-notification-error',
    'subscription-signed-notification-sent',
    'subscription-products-item-identifier-update',
    'subscription-products-item-identifier-error',
    'subscription-products-item-identifier-not-found',
    'subscription-products-item-identifier-change',
    'subscription-missing-notification-url',
    'subscription-missing-customer-email',
    'subscription-missing-customer',
    'subscription-only-upgraded-allowed',
    'subscription-upgraded-notification',
    'subscription-upgrade-failed-notification',
    'subscription-upgrade-archived',
    'subscription-upgrade-child-mismatch',
    'subscription-claim-status-update',
    'subscription-claim-status-update-error',
    'subscription-return-error',
    'subscription-claim-error',
    'subscription-claim-payex-status-update',
    'subscription-claim-payex-capture-error',
    'subscription-claim-missing-customer-email-or-country',
    'subscription-claim-after-approved',
    'subscription-lisa-and-request-error',
    'subscription-lisa-and-response-error',
    'subscription-lisa-and-contract-request-error',
    'subscription-lisa-and-cancel-request-error',
    'subscription-lisa-and-add-imei-request-error',
    'subscription-lisa-and-swap-request-error',
    'subscription-lisa-and-initiate-request-error',
    'subscription-lisa-and-contract-request-response',
    'subscription-lisa-and-contract-cancel-response',
    'subscription-lisa-and-contract-init-response',
    'subscription-lisa-and-contract-imei-response',
    'subscription-lisa-and-swap-start-response',
    'subscription-lisa-and-swap-update-response',
    'subscription-lisa-and-contract-upload-response',
    'subscription-lisa-and-customer-validation-response',
    'subscription-lisa-and-customer-validation-status-response',
    'subscription-lisaand-checkout-callback',
    'subscription-payex-request-error',
    'subscription-payex-response-error',
    'subscription-payment-error',
    'subscription-payment-capture-error',
    'subscription-payment-payment-number-too-long',
    'swap-return-kit-not-received',
    'swap-service-contract-update',
    'swap-service-contract-cancel',
    'swap-service-contract-missing-data',
    'swap-service-contract-missing-insured-value',
    'swap-service-contract-post-error',
    'swap-service-contract-get-error',
    'swap-service-contract-cancel-error',
    'swap-service-contract-get-queue-error',
    'scrive-no-personal-number',
    'scrive-transaction-creation-error',
    'scrive-transaction-invalid-response',
    'scrive-transaction-success',
    'scrive-transaction-invalid-access-url',
    'scrive-transaction-personal-number-not-matching',
    'scrive-document-creation-error',
    'scrive-document-update-error',
    'scrive-document-get-error',
    'scrive-document-signing-data',
    'scrive-document-get-pdf-error',
    'scrive-sign-error',
    'scrive-sign-invalid-party',
    'scrive-sign-invalid-response',
    'scrive-auth-invalid-status',
    'create-subscription-success',
    'create-subscription-error',
    'svea-authenticate-error',
    'svea-credit-check-response',
    'svea-credit-check-error',
    'svea-credit-check-request-error',
    'svea-credit-check-invalid-data',
    'svea-credit-check-allowed-contracts-error',
    'svea-credit-check-debt-error',
    'svea-contract-generation-error',
    'svea-contract-get-error',
    'svea-contract-parse-error',
    'svea-contract-fees-error',
    'svea-contract-fee-creation-error',
    'svea-invoicee-generation-error',
    'svea-invoicee-get-error',
    'svea-invoicee-next-invoice-get-error',
    'svea-invoice-get-error',
    'svea-invoices-error',
    'svea-invoice-pdf-get-error',
    'svea-invoicee-mismatch-error',
    'svea-fetch-error',
    'svea-fee-generation-error',
    'svea-fee-generation',
    'svea-fee-generation-for-return',
    'svea-fee-cancel-error',
    'svea-legacy-trade-in',
    'svea-legacy-trade-in-carry-over-error',
    'svea-missing-info-error',
    'ses-email-send-error',
    'ses-email-template-create-error',
    'ses-email-template-edit-error',
    'ses-email-template-delete-error',
    'sqs-message-processing-error',
    'sqs-generic',
    'sqs-invalid-format',
    'sqs-no-connection',
    'sqs-no-subscriptions-found',
    'sqs-trade-in-multiple-devices-error',
    'sqs-trade-in-diff',
    'sqs-trade-in-diff-negative',
    'sqs-no-trade-in-fee',
    'sqs-trade-in-no-diff-or-negative',
    'sqs-no-matching-subscription',
    'sqs-no-trade-in-service',
    'sqs-trade-in-fee-cancelled-not-set',
    'sqs-no-main-product',
    'sqs-waiting-for-complete',
    'sqs-multiple-subscriptions',
    'sqs-no-svea-contract',
    'trade-in-readjustments',
    'product-missing-imei-on-complete',
    'fraud-check-error',
    'fraud-check-request',
    'subscription-status-change-clear-imei',
    'transaction-time',
    'subscription-return',
    'subscription-return-svea-response',
    'subscription-product-service-warning',
    'subscription-product-return',
    'subscription-product-imei-replace',
    'subscription-product-imei-replace-error',
    'subscription-accessory-return',
    'subscription-partial-fulfillment-error',
    'subscription-partial-fulfillment-complete-error',
    'subscription-partial-shipment-error',
    'subscription-partial-shipment-subscription-status-not-changed',
    'subscription-lisa-and-initiate-rental-agreement',
    'cron-subscriptions-alerts',
    'cron-subscriptions-alerts-failed-to-create',
    'subscription-signed-notification-log',
    'subscription-trade-in-readjustment-already-made',
    'subscription-trade-in-missing-imei',
    'subscription-trade-in-missing-model-info',
    'subscription-trade-in-verification-failed',
    'subscription-trade-in-verification-not-valid',
    'subscription-trade-in-price-quote-failed',
    'subscription-trade-in-creation-failed',
    'subscription-trade-in-creation-disabled',
    'subscription-trade-in-not-supported',
    'subscription-trade-in-error',
    'subscription-contract-end-date-set',
    'subscription-contract-end-date-set-v2',
    'subscription-billing-start-date-set',
    'subscription-billing-end-date-set',
    'customer-has-debt',
    'whitelist-check-failed',
    'subscription-payment-error',
    'payment-method-payex-status-update',
    'mit-update',
    'mit-update-error',
    'automatic-payment-error',
    'automatic-payment-creation-error',
    'manual-trade-in-fix',
    'generic-email-sent',
    'upgrade_notice_45_days_sent',
    'upgrade_notice_30_days_sent',
    'upgrade_notice_15_days_sent',
    'upgrade_notice_7_days_sent',
    'upgrade_notice_1_day_after_sent',
    'upgrade_notice_7_days_after_sent',
    'after_start_60_days_sent',
    'flex_upgrade_notice_sent',
    'upgrade_notice_failed',
    'customer-validation-error',
    'subscription-reversal-error',
    'bulk_payment_incident_emails_sent',
    'partner_email_sent',
    'partner_bulk_email_sent',
    'stating-lisa-flow-error',
    'lisa-and-update-fallback-error',
    'lisa-and-update-parse-error',
    'lisa-and-get-payments-error',
    'price-estimation-failed',
    'lisa-and-archive-pending-parse-error',
    'lisa-and-archive-pending-setting-payment-error',
    'customer-merged',
    'policy-start-date-adjusted',
    'subscription-fpc-api-error',
    'subscription-fpc-create-transaction-error',
    'subscription-fpc-update-transaction-start-date-error',
    'subscription-fpc-update-transaction-imei-error',
    'subscription-fpc-request-error',
    'subscription-fpc-register-trade-in',
    'subscription-fpc-register-trade-in-error',
    'subscription-fpc-trade-in-regenerated',
    'subscription-fpc-trade-in-regenerate-error',
    'subscription-fpc-trade-in-cancel-error',
    'subscription-fpc-cancel-transaction-error',
    'subscription-fpc-create-transaction-n370001-retry',
    'subscription-fpc-register-trade-in-n370001-retry',
    'return-kit-not-received-reminder',
    'bolttech-imei-update-failed',
    'bolttech-device-registration-error',
    'bolttech-device-cancellation-error',
    'bolttech-device-registration-response',
    'bolttech-device-cancellation-response',
    'bolttech-device-cancelled',
    'bolttech-policy-overwritten',
    'lisa-and-insurance-policy-adding-error',
    'subscription-bolttech-response-error',
    'bolttech-device-registration-success',
    'bolttech-policy-status-updated',
    'bolttech-policy-status-check-error',
    'bolttech-policy-missing-imei',
    'bolttech-policy-initialized',
    'subscription-remorse-return-fpc-cancel-error',
    'subscription-remove-upgrade-connection',
    'subscription-external-status-change',
    'subscription-upgrade-device-returned',
    'subscription-device-update-failed-to-get-status-history',
    'subscription-device-returned',
    'subscription-device-returned-in-undefined-state',
    'subscription-device-returned-grade-notification-started',
    'subscription-device-returned-lisa-and-notified',
    'subscription-device-returned-lisa-and-notification-failed',
    'subscription-device-updated-lisa-and-notification-failed',
    'subscription-device-updated-lisa-and-notified',
    'subscription-device-returned-error',
    'subscription-claim-device-received',
    'subscription-upgrade-parent-eligibility-check-failed-on-complete',
    'child-subscription-upgrade-parent-eligibility-check-failed-on-complete',
    'subscription-presign-creation-error',
    'subscription-presign-basket-change',
    'subscription-presign-hybris-body',
    'subscription-presign-price-update',
    'subscription-presign-lisa-and-notify',
    'subscription-presign-lisa-and-notify-error',
    'subscription-presigned-redirect-check',
    'subscription-presigned-redirect-check-error',
    'subscription-presigned-child-subscription-overwritten',
    'subscription-fpc-update-product-name',
    'subscription-fpc-update-product-name-response',
    'subscription-fpc-update-product-name-error',
    'partner-contract-start-date-updated',
    'admin-update-fpc-transaction-id',
    'admin-update-fpc-trade-in-id',
    'admin-delete-fpc-trade-in-device-id',
    'admin-fetch-fpc-trade-in-device-id',
    'subscription-fetch-fpc-trade-in-device-id-error',
] as const

export type LogEvent = (typeof LogEventSchema)[number]

export const TradeinLogSchema = [
    'create',
    'readjustment',
    'multiple-pots',
    'discount-pot-not-found',
    'trade-in-readjustment-w-lisaand',
    'trade-in-readjustment-w-lisaand-response',
    'trade-in-readjustment-w-lisaand-multiple',
    'trade-in-readjustment-w-lisaand-multiple-evaluations',
] as const

export type TradeInLogEvent = (typeof TradeinLogSchema)[number]

export const TradeInPotLogSchema = [
    'relocate',
    'release',
    'recalc-without-subscription',
    'credit-error',
    'credited',
    'relocate-informative',
    'release-informative',
    'recalc-informative',
    'lisa-and-notified',
] as const

export type TradeInPotLogEvent = (typeof TradeInPotLogSchema)[number]

export const AlertEventSchema = [
    ...LogEventSchema,
    'trade-in-manual-intervention-needed',
    'stale-subscription',
] as const

export type AlertEvent = (typeof AlertEventSchema)[number] | `delicate-info-${string}`

export const AdminPaymentLineSchema = zod.object({
    id: zod.number(),
    description: zod.string().optional(),
    lineType: zod.enum(SubscriptionPaymentLineType),
    vatPercentage: zod.string(),
    amountWithVat: zod.string(),
    amountWithoutVat: zod.string(),
    status: zod.enum(SubscriptionPaymentLineStatus),
})

export const AdminPaymentSchema = zod.object({
    id: zod.number(),
    basketId: zod.string().optional(),
    subscriptionUuid: zod.string().optional(),
    type: zod.enum(SubscriptionPaymentType),
    status: zod.enum(SubscriptionPaymentStatus),
    paymentReference: zod.string().optional(),
    totalWithVat: zod.number(),
    paymentLines: zod
        .array(
            zod.object({
                id: zod.number(),
                description: zod.string().optional(),
                lineType: zod.enum(SubscriptionPaymentLineType),
                vatPercentage: zod.string(),
                amountWithVat: zod.string(),
                amountWithoutVat: zod.string(),
                status: zod.enum(SubscriptionPaymentLineStatus),
            }),
        )
        .optional(),
})

export type AdminPayment = zod.infer<typeof AdminPaymentSchema>
export type AdminPaymentLine = zod.infer<typeof AdminPaymentLineSchema>

export const subscriptionInvoiceRowSchema = zod.object({
    uuid: zod.uuid(),

    // https://developer.payex.com/xwiki/wiki/developer/view/Main/Invoicing/invoice-service/invoice-service-apis/Technical%20reference/3.%20Invoice-service/
    number: zod.string().max(15),
    status: zod.enum(SubscriptionInvoiceStatus),

    type: zod.enum(SubscriptionInvoiceType),
    creditedInvoiceUuid: zod.uuid().nullable(),
    tradeInPotUuid: zod.uuid().nullable(),

    customerUuid: zod.uuid(),
    currencyId: zod.number(),

    date: customZod.day(),
    dueDate: customZod.day(),

    totalWithVat: zod.number(),
    totalWithoutVat: zod.number(),
    totalVat: zod.number(),

    /*
     * vatSubTotal has vatRate and vatAmount. How we handle if invoice lines have different vatRates?
     */
    // invoiceVatSpecifications: {}
    sentAt: zod.date(),
    payexInvoiceRef: zod.string().optional(),
    payexInvoicePdf: zod.string().optional(),

    createdAt: zod.date(),
    updatedAt: zod.date().nullable(),
})

export type SubscriptionInvoiceRow = zod.infer<typeof subscriptionInvoiceRowSchema>
export type SubscriptionDTO = {
    hasContract: boolean
    parent?: SubscriptionDTO
    upgrades?: SubscriptionDTO[]
    country?: CountryDTO
    customer?: CustomerDTO
    currency?: CurrencyDTO
    tradeIns?: TradeInDTO[]
    locale?: LocaleDTO
    products?: SubscriptionProductDTO[]
    accessories?: SubscriptionAccessoryDTO[]
    addOns?: SubscriptionAddonDTO[]
    localizedSetAddOns?: SetAddOnDTO[]
    services?: SubscriptionProductServiceDTO[]
    deliveryMethods?: DeliveryMethodDTO[]
    deliveryMethod?: DeliveryMethodDTO
    shippingCountry?: CountryDTO
    possibleAuthenticationMethods?: AuthenticationMethodDTO[]
    fees?: SubscriptionFeeDTO[]
    payments?: SubscriptionPaymentDTO[]
    claims?: SubscriptionClaimDTO[]
    fraudProtectionPassed?: boolean
    canEditName?: boolean
    hasEnded?: boolean
    overrideCanUpgradeDate?: string | null
    upgradeDate?: string | null
} & Omit<SubscriptionRow, 'overrideCanUpgradeDate' | 'upgradeDate'> &
    SubscriptionModelVirtuals &
    SubscriptionSettables
export const authenticationMethodRowSchema = zod.object({
    id: zod.number(),
    providerName: zod.string(),
    bankName: zod.string().optional(),
    visualName: zod.string().optional(),
})

export type AuthenticationMethodRow = zod.infer<typeof authenticationMethodRowSchema>
export type AuthenticationMethodDTO = AuthenticationMethodRow
export const privateCountryRowSchema = zod.object({
    id: zod.number(),
    name: zod.string(),
    code: zod.enum(SupportedCountryCodes),
    vatPercentage: zod.number(),
    customVatPercentage: zod.number().nullable(),
    scriveTemplateId: zod.string().nullable(),
    sveaClientId: zod.string().nullable(),
    maxTotalAccessoryPrice: zod.number().nullable().optional(),
    creditCheckDisabled: zod.boolean(),
    requireUpfrontPayment: zod.boolean(),
    upfrontPaymentNumberOfMonths: zod.number().nonnegative(),
    remorseReturnDays: zod.number(),
    maxBindingContractLength: zod.number().nonnegative().nullable().optional(),
    valuePlanUpgradeMonths: zod.number().nonnegative().nullable().optional(),
    registerTradeInAutomatically: zod.boolean(),
    storeMitToken: zod.boolean(),
    hasOffers: zod.boolean(),
    useExternalEstimation: zod.boolean(),
    fpcSyncEnabled: zod.boolean(),
    fpcReturnDateOffsetDays: zod.number().nonnegative().nullable().optional(),
    policyProvider: zod.enum(PolicyProvider).nullable(),
    invoicingSystem: zod.enum(InvoicingSystem).refine((value) => value !== InvoicingSystem.SVEA, {
        message: 'SVEA support for new Subscriptions is dropped',
    }),
})

export type CountryRow = zod.infer<typeof privateCountryRowSchema>
export type CountryDTO = CountryRow
export const privateCurrencyRowSchema = zod.object({
    id: zod.number(),
    code: zod.string(),
    symbol: zod.string(),
})

export type CurrencyRow = zod.infer<typeof privateCurrencyRowSchema>
export type CurrencyDTO = CurrencyRow
export const customerDTOSchema = zod.object({
    uuid: zod.uuid(),
    personalNumber: zod.string().nullable(),
    fullName: zod.string(),
    firstName: zod.string().nullable(),
    middleName: zod.string().nullable(),
    lastName: zod.string().nullable(),
    email: zod.string().nullable(),
    phone: zod.string().nullable(),
    address: zod.string().nullable(),
    postalCode: zod.string().nullable(),
    county: zod.string().nullable(),
    city: zod.string().nullable(),
})

export const NICKNAME_MAX_LENGTH = 40

export const privateCustomerRowSchema = zod.object({
    ...customerDTOSchema.shape,
    allowedContracts: zod.number().optional(),
    allowedContractsUpdateAt: zod.date().nullable(),
    birthday: zod.string().optional(),
    authMethod: zod.string().nullable(),
    countryId: zod.number().nullable(),
    marketingTerms: zod.date().nullable(),
    sveaInvoiceeId: zod.string().nullable(),
    scrivePersonId: zod.string().nullable(),
    lisaAndCustomerUuid: zod.string().nullable(),
    lisaAndValidationRecommendation: zod.enum(LisaAndValidationRecommendation).nullable(),
    lisaAndValidationReason: zod.enum(LisaAndValidationReason).nullable(),
    lisaAndValidationStatus: zod.enum(LisaAndValidationStatus).nullable(),
    lisaAndValidationIframeUrl: zod.string().nullable(),
    createdAt: zod.date(),
    updatedAt: zod.date(),
    status: zod.enum(CustomerStatus),
    hasOffers: zod.boolean(),
    preApproved: zod.boolean(),
    payexCustomerRef: zod.string().optional().nullable(),

    // Flags for emails
    paymentIncidentEmailSent: zod.boolean().optional(),
})

export type CustomerRow = zod.infer<typeof privateCustomerRowSchema>
export type CustomerDTO = {
    localeCode: string
    visualLocaleCode: string
    country?: CountryDTO
    locale?: LocaleDTO
    currency?: CurrencyDTO
    activeSubscriptions?: SubscriptionDTO[]
    fees?: SubscriptionFeeDTO[]
} & CustomerRow

export type DeliveryMethodRow = {
    uuid: string
    countryId: number
    type: string
}

export type DeliveryMethodDTO = {
    country?: CountryDTO
} & DeliveryMethodRow

export const subscriptionProductRowSchema = zod.object({
    uuid: zod.uuid(),
    nickname: zod.string().min(1).max(NICKNAME_MAX_LENGTH).nullable(),
    subscriptionUuid: zod.uuid(),
    productUuid: zod.uuid().nullable(),
    planPriceId: zod.number().nullable(),
    countryId: zod.number().nullable(),
    currencyId: zod.number().nullable(),
    productSku: zod.string(),
    productImei: zod.string().nullable(),
    productImeiList: zod.array(zod.string()),
    policyProvider: zod.enum(PolicyProvider).nullable(),
    inzmoQueueId: zod.string().nullable(),
    inzmoPolicyId: zod.string().nullable(),
    inzmoPolicyPin: zod.string().nullable(),
    inzmoPolicyStatus: zod.enum(InzmoPolicyStatus).nullable(),
    bolttechPolicyId: zod.string().nullable(),
    bolttechPolicyStatus: zod.enum(BolttechPolicyStatus).nullable(),
    fulfilledQuantity: zod.number(),
    returnedQuantity: zod.number(),
    shippedQuantity: zod.number(),
    quantity: zod.number(),
    quantityUnit: zod.string().nullable(),
    priceWithVat: zod.number(),
    priceWithoutVat: zod.number(),
    vat: zod.number(),
    discount: zod.number(),
    planPriceWithVat: zod.number(),
    planPriceWithoutVat: zod.number(),
    planVat: zod.number(),
    serviceFee: zod.number(),
    durationInMonths: zod.number().nullable(),
    maxBindingContractLength: zod.number().nullable(),
    fpcTransactionProductId: zod.number().nullable(),
    priceList: zod.string(),
})

export type SubscriptionProductRow = zod.infer<typeof subscriptionProductRowSchema>
export type SubscriptionProductDTO = {
    subscription?: SubscriptionDTO
    product?: ProductDTO
    planPrice?: PlanPriceDTO
    country?: CountryDTO
    services?: SubscriptionProductServiceDTO[]
    claims?: SubscriptionClaimDTO[]
    possiblePlans?: PlanPriceDTO[]
} & SubscriptionProductRow

export interface SubscriptionFeeDTO extends SubscriptionFeeRow {
    subscription?: SubscriptionDTO
    subscriptionProduct?: SubscriptionProductDTO
    subscriptionProductService?: SubscriptionProductServiceDTO
    subscriptionAccessory?: SubscriptionAccessoryDTO
    currency?: CurrencyDTO
}

export type SubscriptionPaymentDTO = {
    visualID: string
    paymentLines?: SubscriptionPaymentLineDTO[]
} & SubscriptionPaymentRow

export type LocaleDTO = {
    visualCode?: string
} & LocaleRow
export type SubscriptionAccessoryDTO = {
    accessory?: AccessoryDTO
} & SubscriptionAccessoryRow

export type SubscriptionAddonDTO = {
    slug: string
    price: number
}

export type SubscriptionProductServiceDTO = {
    service?: ServiceDTO
} & SubscriptionProductServiceRow

export type SubscriptionClaimDTO = {
    visualID: string
    damageLocations?: SubscriptionClaimDamageLocationDTO[]
} & SubscriptionClaimRow

export interface UpfrontPayment {
    totalWithVat: number
    totalWithoutVat: number
    totalVat: number
    vatPercentage: number
    paymentLines: Partial<SubscriptionPaymentLineDTO>[]
}

const subscriptionSalesChannelSchema = zod.object({
    type: zod.string().nullable(),
    companyName: zod.string().nullable(),
    salesChannelId: zod.string().nullable(),
})

export const presignOptionsSchema = zod
    .object({
        customerUuid: zod.uuid().optional(),
        parentSubscriptionUuid: zod.uuid().optional(),
        planCode: zod.enum(PlanCode),
        monthlyPlanPrice: zod
            .object({
                priceWithVat: zod.number(),
                priceWithoutVat: zod.number(),
                vat: zod.number(),
            })
            .optional(),
    })
    .optional()

export const privateSubscriptionRowSchema = zod.object({
    uuid: zod.uuid(),

    customerUuid: zod.uuid().nullable(),
    deliveryMethodUuid: zod.uuid().nullable(),
    countryId: zod.number().nullable(),
    currencyId: zod.number().nullable(),
    localeId: zod.number().nullable(),

    status: zod.enum(SubscriptionStatus),
    creditCheck: zod.enum(CreditCheckStatus),
    basketId: zod.string(),
    redirectUrl: zod.string().nullable(),
    notificationUrl: zod.string().nullable(),

    totalPriceWithVat: zod.number(),
    totalPriceWithoutVat: zod.number(),
    totalVat: zod.number(),
    totalDiscount: zod.number(),

    jwtToken: zod.string().nullable(),
    salesChannel: subscriptionSalesChannelSchema.nullable(),
    monthlyPayments: zod.string().nullable(),

    // Set during onSubscriptionComplete with new Date() - so using server tz
    contractStartDatetime: zod.date().nullable(),
    isStartDateSetByPartner: zod.boolean().nullable(),
    contractEndDatetime: zod.date().nullable(),

    invoicingSystem: zod.enum(InvoicingSystem),

    // TODO: Setting these during actual events not through API
    billingStartDatetime: zod.date().nullable().optional(),
    billingEndDatetime: zod.date().nullable().optional(),

    differentShippingAddress: zod.boolean(),
    shippingAddress: zod.string().nullable(),
    shippingPostalCode: zod.string().nullable(),
    shippingCounty: zod.string().nullable(),
    shippingCity: zod.string().nullable(),
    shippingCountryId: zod.number().nullable(),

    fraudProtectionPassed: zod.boolean().nullable(),

    parentSubscriptionUuid: zod.uuid().nullable(),
    childSubscriptionUuid: zod.uuid().nullable(),
    lisaAndRentalAgreementId: zod.string().nullable(),
    lisaAndRentalAgreementStatus: zod.enum(LisaAndRentalAgreementStatus).nullable(),
    sveaContractId: zod.string().nullable(),
    scriveDocumentId: zod.string().nullable(),
    scriveSignatoryId: zod.string().nullable(),
    scriveSignRedirectUrl: zod.string().nullable(),
    mitToken: zod.string().nullable(),
    mitTokenName: zod.string().nullable(),
    mitExpiryDate: customZod.day().nullable(),
    mitDetails: paymentOrderPaidDetailsSchema.nullable(),

    upgradeReminder45Sent: zod.boolean().nullable(),
    upgradeReminder30Sent: zod.boolean().nullable(),
    upgradeReminderFlexSent: zod.boolean().nullable(),
    upgradeReminder15Sent: zod.boolean().nullable(),
    upgradeReminder7Sent: zod.boolean().nullable(),
    upgradeReminder1AfterSent: zod.boolean().nullable(),
    upgradeReminder7AfterSent: zod.boolean().nullable(),

    genericEmailFlags: zod.record(zod.string(), zod.date().nullable()),
    afterStart60DaysSent: zod.boolean().nullable(),

    nonQueryableMeta: zod.object({
        estimatedTradeInValue: zod.number().nullable(),
        childPendingPaymentLink: zod.string().nullable(),
        childPendingPaymentLinkExpiresAt: zod.date().nullable(),
        presignOptions: presignOptionsSchema,
    }),

    pendingPaymentLink: zod.string().nullable(),
    pendingPaymentLinkExpiresAt: zod.date().nullable(),

    overrideCanUpgradeDate: customZod.day().nullable(),

    overrideCanUpgrade: zod.boolean().nullable(),
    overrideCanSwap: zod.boolean().nullable(),
    overrideCanCancel: zod.boolean().nullable(),

    fpcTransactionId: zod.number().nullable(),
    fpcTradeInId: zod.number().nullable(),
    fpcTradeInDeviceId: zod.number().nullable(),
    fpcReturnChangelogHash: zod.string().nullable(),

    vendorNotificationStatus: zod.enum(VendorNotificationStatus).nullable(),

    createdAt: zod.date(),
    updatedAt: zod.date(),
})

export type SubscriptionRow = zod.infer<typeof privateSubscriptionRowSchema>

export interface SubscriptionModelVirtuals {
    hasContract: boolean
    hasTokenGenerated: boolean
    canEditName: boolean
    hasEnded: boolean

    upgradeDate: Day | null
    cancelDate: Date | null

    isEligibleForUpgradeByDate: boolean
    isEligibleForCancelByDate: boolean
    isEligibleForSwapByDate: boolean

    localizedPossibleAddons: SetAddOnDTO[]
    localizedSetAddOns: SetAddOnDTO[]
}

export interface MessageModelDTO {
    id: number
    basketId: string
    // PartnerDeviceInfo.CP_WithVAT: Real discount amount provided by us
    // VendorDeviceInfo.CP_WithVAT : Potential amount provided by the vendor
    message: string
    createdAt: Date
    updatedAt: Date
    status: MessageStatus
    tradeInUuid?: string
}

export interface SubscriptionSettables {
    permissions?: SubscriptionModelPermissions
    messages?: MessageModelDTO[]
    child?: SubscriptionDTO
    sveaFees?: ExtendedSveaFee[]
}

export type ProductRow = {
    uuid: string
    sku: string
    type: ProductType
    coveragePercentage: number
    fpcItemVariantId: number | null
    fpcItemVariantName: string | null
    imageUrl: string
    rrpEur: number
    color: string
    capacity: string
    status: ProductStatus
    launchDate: Day | null
}

export type ProductDTO = {
    translation?: ProductTranslationDTO
    translations?: ProductTranslationDTO[]
    logs?: AdminPriceListLogModel[]
    description?: string
} & ProductRow

export type PlanPriceRow = {
    id: number
    planUuid: string
    productUuid: string
    countryId: number
    currencyId: number
    serviceFee: number
    priceWithVat: number
    priceWithoutVat: number
    vat: number
    priceList: string
}

export type PlanPriceDTO = {
    plan?: PlanDTO
    currency?: CurrencyDTO
    country?: CountryDTO
    product?: ProductDTO
    priceList: string
} & PlanPriceRow

export type SubscriptionFeeRow = {
    id: number

    customerUuid: string
    subscriptionUuid: string
    subscriptionProductUuid?: string
    subscriptionProductServiceId?: number
    subscriptionAccessoryId?: number
    tradeInPotUuid?: string
    currencyId?: number

    periodType: SubscriptionFeePeriodType
    feeType: SubscriptionFeeType

    vatPercentage: number

    // Unit price
    priceWithVat: number
    priceWithoutVat: number
    priceVat: number

    // If startDate is NULL, the fee is not yet active
    startDate?: Day
    endDate?: Day
    invoicedTo?: Day

    status: SubscriptionFeeStatus
    refundedByEvent?: string

    customDescription?: string
    sveaFields?: {
        bound_months?: number
        cancelled_date?: string
        end_date?: string
    }

    createdAt: Date
    updatedAt: Date | null
}

export const paymentOperationSchema = zod.object({
    method: zod.string(),
    href: zod.string(),
    rel: zod.string(),
    contentType: zod.string(),
})

export const paymentOrderResponseSchema = zod.object({
    paymentOrder: zod.object({
        id: zod.string(),
        status: zod.enum(PayexPaymentOrderStatus),
        amount: zod.int(),
        vatAmount: zod.int(),
        /* these exist, but we actually don't care about these if I am not mistaken, so we do not need to validate these
        created: zod.string(),
        updated: zod.string(),
        operation: zod.string(),
        amount: zod.int(), // E.g.: 10000 = 100.00 SEK, 5000 = 50.00 SEK
        vatAmount: zod.int(), // E.g.: 10000 = 100.00 SEK, 5000 = 50.00 SEK
        description: zod.string().max(40),
        initiatingSystemUserAgent: zod.string(),
        language: zod.string(),
        availableInstruments: zod.string(),
        integration: zod.string(),
        instrumentMode: zod.boolean(),
        guestMode: zod.boolean(),
        */
        paid: zod
            .object({
                id: zod.string(),
                number: zod.number(),
                instrument: zod.string(),
                payeeReference: zod.string(),
                transactionType: zod.string(),
                amount: zod.number(),
                submittedAmount: zod.number(),
                feeAmount: zod.number(),
                discountAmount: zod.number(),
                paymentTokenGenerated: zod.boolean(),
                tokens: zod
                    .array(
                        zod.object({
                            type: zod.enum(PayexTokenType),
                            token: zod.string(),
                            name: zod.string(),
                            expiryDate: zod.string(),
                        }),
                    )
                    .optional(),
                details: paymentOrderPaidDetailsSchema.optional(),
            })
            .optional(),
    }),
    operations: zod.array(paymentOperationSchema),
})

export const subscriptionPaymentRowSchema = zod.object({
    id: zod.int().positive(),

    subscriptionUuid: zod.uuid(),
    countryId: zod.number().nullable(),
    currencyId: zod.number().nullable(),

    number: zod
        .string()
        .max(30)
        .regex(/A-Za-z0-9/),

    paymentType: zod.enum(SubscriptionPaymentType),
    paymentStatus: zod.enum(SubscriptionPaymentStatus),
    paymentResponse: paymentOrderResponseSchema.optional(),
    paymentReference: zod.string(),

    vatPercentage: zod.number(),
    totalWithVat: zod.number(),
    totalWithoutVat: zod.number(),
    totalVat: zod.number(),

    createdAt: zod.date(),
    updatedAt: zod.date(),
})

export type SubscriptionPaymentRow = zod.infer<typeof subscriptionPaymentRowSchema>

export type AccessoryRow = {
    uuid: string
    sku: string
    imageUrl: string
}

export type AccessoryDTO = {
    translations?: AccessoryTranslationDTO[]
    translation?: AccessoryTranslationDTO
} & AccessoryRow

export type SubscriptionClaimLogRow = {
    id: number
    time: Date
    event: string
    details: Record<string, unknown>
    subscriptionClaimId: number | null
    claimReviewerUuid: string | null
}

export const subscriptionClaimRowSchema = zod.object({
    id: zod.int().positive(),

    claimType: zod.enum(ClaimType),
    customerUuid: zod.uuid(),
    subscriptionProductUuid: zod.uuid(),
    claimReviewerUuid: zod.uuid().nullable(),

    claimStatus: zod.enum(ClaimStatus),
    productResolution: zod.enum(ClaimProductResolution).nullable(),
    productImei: zod.string(),
    damageType: zod.enum(ClaimDamageType).nullable(),
    incidentDate: zod.iso.datetime().nullable(),
    incidentDescription: zod.string().nullable(),

    redirectUrl: customZod.httpUrl().nullable(),

    payexId: zod.string().nullable(),
    payexStatus: zod.enum(PayexPaymentOrderStatus).nullable(),

    isDamaged: zod.boolean(),
    termsAccepted: zod.boolean(),

    returnKitSentAt: zod.date().nullable(),
    automatedCancelDeadline: zod.date().nullable(),

    externalClaimId: zod.string().nullable(),
    externalClaimStatus: zod.string().nullable(),
    externalClaimProvider: zod.enum(PolicyProvider).nullable(),

    createdAt: zod.date(),
    updatedAt: zod.date(),
})

export type SubscriptionClaimRow = zod.infer<typeof subscriptionClaimRowSchema>

export type AccessoryTranslationRow = {
    id: number
    localeId: number
    accessoryUuid: string
    name: string | null
}

export type AccessoryTranslationDTO = AccessoryTranslationRow

export interface SubscriptionModelPermissions {
    activePerson: boolean
    canUpgrade: boolean
    canSwap: boolean
    canCancel: boolean
    parts?: { [key: string]: { [key: string]: boolean } }
}

export interface ExtendedSveaFee extends SveaFee {
    billed_to: string | null
    bound_to: string | null
    cancellation_period_months: number | null
    cancelled_at: string | null
    cancelled_by: string | null
    created_by: string | null
    created_time: string
    fee_number: number
    fee_template_nr: number | null
    final_invoice_sent: boolean
    last_invoice_number: number | null
    next_invoice_fee_amount: number | null
    next_invoice_from: string | null
    next_invoice_include: boolean
    next_invoice_to: string | null
    obliterated: boolean
    remaining_bound_months: number | null
}

export type ProductTranslationRow = {
    id: number
    localeId: number
    productUuid: string
    name: string | null
    description: string | null
}

export type ProductTranslationDTO = ProductTranslationRow

export type PlanRow = {
    uuid: string
    code: PlanCode
    durationInMonths: number
    hasInsurance: boolean
    isDisabled: boolean
}

export type PlanDTO = {
    prices?: PlanPriceDTO[]
} & PlanRow

export interface SveaFee {
    account_no: number
    client_cross_ref: string
    description: string
    period: 'once' | 'monthly'
    vat_rate_percent: number
    months_in_advance: number

    amount_excl_vat: number
    start_date: string
    bound_months?: number
    cancelled_date?: string
    end_date?: string
}

export interface SubscriptionAddressReference {
    uuid: string
    invoicingSystem: InvoicingSystem
}

export interface SubscriptionAddress {
    references: SubscriptionAddressReference[]
    fullAddress: string
    address: string
    city: string
    postalCode: string
    county: string
}

export const fpcTradeInReportingStatusLog = zod.array(
    zod.object({
        StatusId: zod.number(),
        StatusName: zod.string(),
        Timestamp: zod.string(), // "2026-01-22T11:17:38.734Z"
    }),
)

export type FpcTradeInReportingStatusLog = zod.infer<typeof fpcTradeInReportingStatusLog>
